/**
 * @typedef {'top'|'right'|'bottom'|'left'|'center'} Placement
 *
 * @typedef {Object} TourStep
 * @property {string | (() => HTMLElement | null)} target - CSS selector or a function that returns the target element.
 * @property {(string | (() => HTMLElement | null))[]} [extraTargets]
 * @property {React.ReactNode} [title]
 * @property {React.ReactNode} [description]
 * @property {Placement} [placement]
 * @property {number} [offset] - Padding (px) around the spotlight hole.
 * @property {boolean} [disableInteraction] - Temporarily disable pointer events on the target while the step is active.
 * @property {{x?:number,y?:number}} [popoverOffset] - Extra shift in pixels applied to the popover position (e.g. `{x:-120}` to shift left when placement is `bottom`).
 * @property {(ctx:{rect:{top,left,width,height}|null, placement:Placement, gap:number, viewport:{width,height}})
 *             => {top:number,left:number,transform?:string}} [getPopoverPos]
 *    Custom function to fully control popover coordinates for this step.
 *
 * @callback RectResolver
 * @param {TourStep} step - Current step definition.
 * @param {HTMLElement|null} targetEl - Resolved target element if available.
 * @returns {{top:number,left:number,width:number,height:number} | {top:number,left:number,width:number,height:number}[] | null}
 *
 * @typedef {Object} TourProps
 * @property {TourStep[]} steps
 * @property {number} [current] - Controlled index of the active step.
 * @property {number} [initialStep] - Uncontrolled starting step index.
 * @property {boolean} [open] - Whether the tour is visible.
 * @property {(index:number)=>void} [onChange]
 * @property {()=>void} [onClose]
 * @property {number} [zIndex]
 * @property {boolean} [maskClosable]
 * @property {string} [className]
 * @property {RectResolver} [rectResolver] - Extension point for custom spotlight rects (e.g. canvas areas).
 */

/**
 * @typedef {{top:number,left:number,width:number,height:number}} VRect
 *
 * RectResolver may return ONE rect, MANY rects, or null.
 * @callback RectResolver
 * @param {any} step
 * @param {HTMLElement|null} targetEl
 * @returns {VRect | VRect[] | null}
 */

import {getLocalData, setLocalData} from '../services/StorageServices'

/**
 * Resolve a target to an HTMLElement.
 * @param {string | (()=>HTMLElement|null) | HTMLElement | null} t
 * @returns {HTMLElement|null}
 */
export const getEl = (t) => {
    if (typeof t === 'string') return document.querySelector(t)
    if (typeof t === 'function') {
        try {
            return t()
        } catch {
            return null
        }
    }
    return t || null
}
/**
 * Clamp a number between min and max.
 * @param {number} v
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

/**
 * Compute a padded viewport-space rect around an element.
 * @param {HTMLElement} el
 * @param {number} [pad=8]
 * @returns {{top:number,left:number,width:number,height:number,raw:DOMRect}}
 */
export const getViewportRect = (el, pad = 8) => {
    const r = el.getBoundingClientRect()
    const top = Math.max(0, r.top - pad)
    const left = Math.max(0, r.left - pad)
    const right = Math.min(window.innerWidth, r.right + pad)
    const bottom = Math.min(window.innerHeight, r.bottom + pad)
    return {top, left, width: right - left, height: bottom - top, raw: r}
}

/**
 * Compute popover anchor position and transform relative to a spotlight rect.
 * @param {{top:number,left:number,width:number,height:number}|null} rect
 * @param {Placement} placement
 * @param {number} [gap=12]
 * @param {{x?:number,y?:number}} [offset] - Additional x/y shift in CSS pixels applied after base placement.
 * @returns {{top:number,left:number,transform:string}}
 */
export const computePopoverPos = (rect, placement, gap = 12, offset = {x: 0, y: 0}) => {
    const vw = window.innerWidth,
        vh = window.innerHeight
    if (!rect) return {top: vh / 2, left: vw / 2, transform: 'translate(-50%, -50%)'}
    const {top, left, width, height} = rect
    let x = left + width / 2,
        y = top + height / 2,
        transform = 'translate(-50%, -50%)'
    switch (placement) {
        case 'top':
            y = top - gap
            transform = 'translate(-50%, -100%)'
            break
        case 'bottom':
            y = top + height + gap
            transform = 'translate(-50%, 0)'
            break
        case 'left':
            x = left - gap
            transform = 'translate(-100%, -50%)'
            break
        case 'right':
            x = left + width + gap
            transform = 'translate(0, -50%)'
            break
        default:
            break // center
    }
    x = clamp(x, 8, vw - 8)
    y = clamp(y, 8, vh - 8)
    x += offset?.x || 0
    y += offset?.y || 0
    return {top: y, left: x, transform}
}

/** Pad and clamp a box with sides into a viewport rect */
export function padClampSides(r, pad = 8, radius = 0) {
    const top = Math.max(0, r.top - pad)
    const left = Math.max(0, r.left - pad)

    const right = Math.min(window.innerWidth, r.left + r.width + pad)
    const bottom = Math.min(window.innerHeight, r.top + r.height + pad)
    return {top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top), radius}
}

export function unionRects(rects) {
    if (!rects?.length) return null
    let L = Infinity,
        T = Infinity,
        R = -Infinity,
        B = -Infinity
    rects.forEach(({top, left, width, height}) => {
        L = Math.min(L, left)
        T = Math.min(T, top)
        R = Math.max(R, left + width)
        B = Math.max(B, top + height)
    })
    return {top: T, left: L, width: R - L, height: B - T}
}

// --- Rect helpers ---

export function addBridges(rects, threshold = 12) {
    const out = rects.slice()
    const right = (r) => r.left + r.width
    const bottom = (r) => r.top + r.height

    for (let i = 0; i < rects.length; i++) {
        for (let j = i + 1; j < rects.length; j++) {
            const a = rects[i],
                b = rects[j]

            // Horizontal bridge (elements side by side)
            const xGap = a.left <= b.left ? b.left - right(a) : a.left - right(b)
            const yTop = Math.max(a.top, b.top)
            const yBot = Math.min(bottom(a), bottom(b))
            if (xGap > 0 && xGap <= threshold && yBot > yTop) {
                const left = a.left <= b.left ? right(a) : right(b)
                out.push({left, top: yTop, width: xGap, height: yBot - yTop})
                continue // no need to also check vertical bridge for this pair
            }

            // Vertical bridge (one above the other)
            const yGap = a.top <= b.top ? b.top - bottom(a) : a.top - bottom(b)
            const xL = Math.max(a.left, b.left)
            const xR = Math.min(right(a), right(b))
            if (yGap > 0 && yGap <= threshold && xR > xL) {
                const top = a.top <= b.top ? bottom(a) : bottom(b)
                out.push({left: xL, top, width: xR - xL, height: yGap})
            }
        }
    }
    return out
}

// Run a maybe-async hook; if it returns false, treat as blocked
export async function runHook(hook, ctx) {
    if (typeof hook !== 'function') return true
    const res = hook(ctx)
    return typeof res === 'boolean' ? res : ((await res) ?? true)
}
// Run per-step and global versions; if any returns false, block
export async function runBoth(stepHook, globalHook, ctx) {
    const a = await runHook(stepHook, ctx)
    if (a === false) return false
    const b = await runHook(globalHook, ctx)
    return b !== false
}

export function resolveCanvasFlexible(step /*, targetEl */) {
    const pad = step?.offset ?? 8
    const out = []

    if (step?.isMulti && Array.isArray(step.els)) {
        for (const t of step.els) {
            const el = getEl(t)
            if (!el) continue
            out.push(padClampSides(el.getBoundingClientRect(), pad))
        }
    }

    if (step?.canvas && Array.isArray(step.canvasRegions) && step.canvasRegions.length) {
        const canvas = getEl(step.canvas)
        if (canvas) {
            const box = canvas.getBoundingClientRect()
            const cssW = box.width,
                cssH = box.height
            const devW = canvas.width,
                devH = canvas.height
            const vw = window.innerWidth,
                vh = window.innerHeight

            // for 'canvas' space (device/world px): convert to canvas CSS px
            const ratioX = devW > 0 && cssW > 0 ? devW / cssW : NaN
            const ratioY = devH > 0 && cssH > 0 ? devH / cssH : NaN

            const ctx = {
                vw,
                vh,
                canvasBox: box,
                canvasCss: {w: cssW, h: cssH},
                canvasDev: {w: devW, h: devH},
                ratioX,
                ratioY,
            }

            for (let spec of step.canvasRegions) {
                // allow function region
                if (typeof spec === 'function') spec = spec(ctx)
                if (!spec) continue

                const space = spec.space || 'canvas' // default to 'canvas' for backward compat
                const origin = spec.origin || 'top-left' // or 'center'
                const w = dim(spec.w ?? spec.width, spaceBaseWidth(space, ctx))
                const h = dim(spec.h ?? spec.height, spaceBaseHeight(space, ctx))
                if (!(w > 0 && h > 0)) continue

                // parse x/y in chosen space
                let x = dim(spec.x, spaceBaseWidth(space, ctx))
                let y = dim(spec.y, spaceBaseHeight(space, ctx))

                if (origin === 'center') {
                    x -= w / 2
                    y -= h / 2
                }

                // Map to viewport CSS px
                let left,
                    top,
                    width = w,
                    height = h
                if (space === 'viewport') {
                    // already viewport CSS
                    left = x
                    top = y
                } else if (space === 'canvas-css') {
                    // CSS px inside canvas box -> offset to viewport
                    left = box.left + x
                    top = box.top + y
                } else {
                    // 'canvas' => internal/world px
                    if (typeof step.toScreen === 'function') {
                        // world -> canvas CSS px (relative to canvas)
                        const p0 = step.toScreen({x, y})
                        const p1 = step.toScreen({x: x + w, y: y + h})
                        if (!isFiniteNum(p0?.x) || !isFiniteNum(p0?.y) || !isFiniteNum(p1?.x) || !isFiniteNum(p1?.y))
                            continue
                        left = box.left + Math.min(p0.x, p1.x)
                        top = box.top + Math.min(p0.y, p1.y)
                        width = Math.abs(p1.x - p0.x)
                        height = Math.abs(p1.y - p0.y)
                    } else {
                        // device/world px -> canvas CSS px via DPR ratios
                        if (!isFiniteNum(ratioX) || !isFiniteNum(ratioY)) continue // canvas not laid out yet
                        left = box.left + x / ratioX
                        top = box.top + y / ratioY
                        width = w / ratioX
                        height = h / ratioY
                    }
                }

                out.push(padClampSides({top, left, width, height}, pad))
            }
        }
    }

    return out.length ? out : null
}
function isFiniteNum(v) {
    return typeof v === 'number' && Number.isFinite(v)
}
function spaceBaseWidth(space, {vw, canvasCss, canvasDev}) {
    return space === 'viewport' ? vw : space === 'canvas-css' ? canvasCss.w : canvasDev.w
}
function spaceBaseHeight(space, {vh, canvasCss, canvasDev}) {
    return space === 'viewport' ? vh : space === 'canvas-css' ? canvasCss.h : canvasDev.h
}

// parse numbers or strings like '60%', '40vw', '30vh'
function dim(val, base) {
    if (val == null) return NaN
    if (typeof val === 'number') return val
    const s = String(val).trim().toLowerCase()
    if (s.endsWith('vw')) return (parseFloat(s) / 100) * window.innerWidth
    if (s.endsWith('vh')) return (parseFloat(s) / 100) * window.innerHeight
    if (s.endsWith('%')) return (parseFloat(s) / 100) * base
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : NaN
}

export const markUserCompletedGuide = (mockupType) => {
    const key = `completed-guide-${mockupType}`
    setLocalData(key, 1)
}

export const hasUserCompletedGuide = (mockupType) => {
    const key = `completed-guide-${mockupType}`
    return getLocalData(key)
}
